/**
 * COMMERCE-WORKSPACE-FRAMEWORK-000001 — configurable summary strip.
 * Domains supply metrics only; layout/honesty styling is shared.
 */
import type { SummaryMetric } from './types';

function Metric({ label, value, hint, emphasis }: SummaryMetric) {
  return (
    <div
      className={`min-w-[7.5rem] flex-1 rounded-[var(--shell-radius-md)] border border-[var(--shell-border)] bg-[var(--shell-surface-elevated)] px-3 py-2.5 ${
        emphasis ? 'ring-1 ring-[var(--shell-accent)]/30' : ''
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--shell-fg-subtle)]">
        {label}
      </p>
      <p
        className={`mt-0.5 tabular-nums text-xl font-semibold tracking-tight ${
          value === 'N/A' ? 'text-[var(--shell-fg-muted)]' : 'text-[var(--shell-fg)]'
        }`}
      >
        {value}
      </p>
      {hint ? (
        <p className="mt-0.5 text-[10px] leading-snug text-[var(--shell-fg-muted)]">{hint}</p>
      ) : null}
    </div>
  );
}

export function WorkspaceSummaryStrip({
  title,
  ariaLabel,
  metrics,
  source,
  lastSync,
  note,
}: {
  title: string;
  ariaLabel?: string;
  metrics: SummaryMetric[];
  source?: string | null;
  lastSync?: string | null;
  note?: string | null;
}) {
  return (
    <section aria-label={ariaLabel || title} className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--shell-fg-subtle)]">
          {title}
        </h2>
        <p className="text-[11px] text-[var(--shell-fg-muted)]">
          {source ? <span className="font-mono">{source}</span> : null}
          {source && lastSync ? ' · ' : null}
          {lastSync ? <>Last sync {new Date(lastSync).toLocaleString()}</> : null}
          {!source && !lastSync ? 'Source · MarketSync when loaded' : null}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {metrics.map((m) => (
          <Metric key={m.id} {...m} />
        ))}
      </div>
      {note ? (
        <p className="text-[11px] text-[var(--shell-fg-muted)]" role="note">
          {note}
        </p>
      ) : null}
    </section>
  );
}
